import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  entidadesApi, contactosApi, gruposApi, referenciaApi, usuariosApi,
  contratosApi, catalogosApi, municipiosApi, visitasApi, tareasApi,
  resolveAssetUrl,
} from '../services/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import AiButton from '../components/AiButton';
import EmpresaAutocomplete from '../components/empresas/EmpresaAutocomplete';
import PerplexityBuscarModal from '../components/PerplexityBuscarModal';
import AiProposal from '../components/AiProposal';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, Building2, MapPin, Mail, Pencil, Save, X,
  UserCog, Briefcase, Users as UsersIcon, Activity,
  FileText, FileStack, History, ArrowRightLeft, MapPinned, ShieldAlert,
  CalendarDays, ListChecks, Sparkles,
  BarChart3, LayoutDashboard, GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';
import CorreosTab from '../components/cliente/CorreosTab';
import Vista360 from '../components/cliente/Vista360';
import DelegacionesTab from '../components/cliente/DelegacionesTab';
import { TareasTab } from '../components/cliente/TareasTab';
import { HistorialTab } from '../components/cliente/HistorialTab';
import { VisitasTab } from '../components/cliente/VisitasTab';
import { ActividadTab } from '../components/cliente/ActividadTab';
import { DatosTab } from '../components/cliente/DatosTab';
import { ContactosTab } from '../components/cliente/ContactosTab';
import { ContratosTab } from '../components/cliente/ContratosTab';
import {
  Campo, Select, SubTab, ActividadItem,
  buildTree, formatDate,
  ESTADO_STYLE, TIPO_LABEL, NIVELES_RELACION,
  emptyEntidad, emptyContacto,
} from '../components/cliente/_shared';
import ResumenContactoIA from '../components/ResumenContactoIA';
import PreguntarBuzon from '../components/PreguntarBuzon';
import { VisitaFormDialog } from '../components/visitas/VisitaFormDialog';
import VentasFicha360 from '../components/cliente/VentasFicha360';
import UteTrazabilidad from '../components/cliente/UteTrazabilidad';
import { ConfirmDeleteDialog } from '../components/ui/ConfirmDeleteDialog';
import { FieldError } from '../components/ui/FieldError';
import { isValidEmail, required } from '../utils/validation';

const ClienteDetalle = () => {
  const { id } = useParams();
  const isNuevo = id === 'nuevo';
  const navigate = useNavigate();
  const { user, isAdmin, hasPermission } = useAuth();
  const isPrivileged = user?.rol === 'admin' || user?.rol === 'dir_comercial';

  const [entidad, setEntidad] = useState(null);
  const [form, setForm] = useState(() => {
    const base = emptyEntidad();
    // Pre-rellenar si viene de "Nueva delegación" desde una empresa matriz
    // Usamos window.location.search directamente para evitar TDZ con searchParams
    const params = new URLSearchParams(window.location.search);
    const parentId = params.get('parent_id');
    if (parentId) {
      base.es_delegacion = true;
      base.parent_entidad_id = parentId;
    }
    return base;
  });
  const [edit, setEdit] = useState(isNuevo);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'datos');

  const [grupos, setGrupos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [provinciasPorPais, setProvinciasPorPais] = useState({ ES: [], PT: [] });
  const [paisesExtra, setPaisesExtra] = useState([]); // [{codigo,nombre}] sin ES/PT
  const [comerciales, setComerciales] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [funciones, setFuncionesCat] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [usuariosAll, setUsuariosAll] = useState([]);

  // Quick-create grupo dialog
  const [grupoDialog, setGrupoDialog] = useState(false);
  const [grupoForm, setGrupoForm] = useState({ nombre: '', descripcion: '' });
  const [grupoErr, setGrupoErr] = useState('');
  const [savingGrupo, setSavingGrupo] = useState(false);

  const [contactos, setContactos] = useState([]);
  const [delegaciones, setDelegaciones] = useState([]);
  const [actividad, setActividad] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [visitas, setVisitas] = useState([]);
  const [tareasCliente, setTareasCliente] = useState([]);
  const [contactoDelegacionId, setContactoDelegacionId] = useState('');

  // Confirmación unificada de borrado (contacto, contrato, visita, tarea)
  const [confirmDel, setConfirmDel] = useState({ open: false, item: null, tipo: null, nombre: '' });
  const confirmarBorrarUnificado = async () => {
    if (!confirmDel.item) return;
    try {
      if (confirmDel.tipo === 'contacto') {
        await contactosApi.delete(confirmDel.item.id);
        cargarEntidad();
      } else if (confirmDel.tipo === 'contrato') {
        await contratosApi.delete(confirmDel.item.id);
        cargarEntidad();
      } else if (confirmDel.tipo === 'visita') {
        await visitasApi.delete(confirmDel.item.id);
        cargarEntidad();
      } else if (confirmDel.tipo === 'tarea') {
        await tareasApi.delete(confirmDel.item.id);
        recargarTareas();
      }
    } catch (e) { toast.error(e.message); }
  };

  // Contacto dialog
  const [contactoDialog, setContactoDialog] = useState(false);
  const [contactoEditing, setContactoEditing] = useState(null);
  const [contactoForm, setContactoForm] = useState(emptyContacto());
  const [contactoErr, setContactoErr] = useState('');
  const [contactoTabInner, setContactoTabInner] = useState('principal'); // principal | personal | trayectoria

  // Cambiar empresa dialog
  const [cambioEmpresaDialog, setCambioEmpresaDialog] = useState(false);
  const [cambioEmpresaForm, setCambioEmpresaForm] = useState({ nueva_entidad_id: '', nuevo_cargo: '', nuevo_departamento: '', fecha_cambio: '' });
  const [cambioEmpresaSearch, setCambioEmpresaSearch] = useState('');
  const [cambioErr, setCambioErr] = useState('');

  // Contrato dialog
  const [contratoDialog, setContratoDialog] = useState(false);
  const [contratoEditing, setContratoEditing] = useState(null);
  const [contratoForm, setContratoForm] = useState({
    nombre: '', codigo_interno: '',
    fecha_inicio: '', fecha_fin: '', estado: 'activo',
    proxima_licitacion_fecha: '', licitacion_gestion_notas: '',
    notas: '',
  });
  const [contratoMunis, setContratoMunis] = useState([]); // [{codigo_ine, nombre, provincia}]
  const [muniBuscarTexto, setMuniBuscarTexto] = useState('');
  const [muniBuscarResult, setMuniBuscarResult] = useState([]);
  const [contratoErr, setContratoErr] = useState('');

  // Visita dialog
  const [visitaDialog, setVisitaDialog] = useState(false);
  const [visitaEditing, setVisitaEditing] = useState(null);
  const [pplxOpen, setPplxOpen] = useState(false);

  const recargarTareas = async () => {
    try { const ts = await tareasApi.list({ entidad_id: id }); setTareasCliente(ts); } catch {}
  };

  // Tarea derivada dialog
  const [tareaDialog, setTareaDialog] = useState(false);
  const [tareaSourceVisita, setTareaSourceVisita] = useState(null);
  const [tareaEditing, setTareaEditing] = useState(null);
  const [tareaForm, setTareaForm] = useState({
    titulo: '', descripcion: '', vencimiento: '', asignado_user_id: '', prioridad: 'media',
  });
  const [tareaErr, setTareaErr] = useState('');

  // Toggle estado tarea (rápido) usado por TareasTab
  const toggleTareaEstado = async (t) => {
    const next = t.estado === 'hecha' ? 'pendiente' : 'hecha';
    try { await tareasApi.update(t.id, { estado: next }); recargarTareas(); }
    catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [gs, provs, deps, fns, us, mats, ppp, paisesAll] = await Promise.all([
          gruposApi.list().catch(() => []),
          referenciaApi.provincias().catch(() => []),
          catalogosApi.list('departamentos_contacto').catch(() => []),
          catalogosApi.list('funciones_contacto').catch(() => []),
          isAdmin ? usuariosApi.list({ estado: 'activo' }).catch(() => []) : usuariosApi.list({ estado: 'activo' }).catch(() => []),
          catalogosApi.list('materiales_fabricados').catch(() => []),
          referenciaApi.provinciasPorPais().catch(() => ({ ES: [], PT: [] })),
          referenciaApi.paises().catch(() => []),
        ]);
        setGrupos(gs); setProvincias(provs); setDepartamentos(deps); setFuncionesCat(fns);
        setUsuariosAll(us);
        setMateriales(mats);
        setProvinciasPorPais(ppp || { ES: [], PT: [] });
        setPaisesExtra(paisesAll || []);
        if (isAdmin) {
          setComerciales(us.filter((u) => u.rol === 'comercial'));
        }
      } catch (_) {}
    })();
  }, [isAdmin]);

  useEffect(() => {
    if (isNuevo) return;
    cargarEntidad();
    // eslint-disable-next-line
  }, [id]);

  const esGestora = (form.tipo === 'gestora') || (entidad?.tipo === 'gestora');

  const cargarEntidad = async () => {
    try {
      const e = await entidadesApi.get(id);
      setEntidad(e);
      setForm({
        razon_social: e.razon_social || '',
        nombre_comercial: e.nombre_comercial || '',
        cif: e.cif || '',
        codigo_cliente_erp: e.codigo_cliente_erp || '',
        tipo: e.tipo || 'otro',
        estado: e.estado || 'prospecto',
        grupo_id: e.grupo_id || '',
        es_cliente: !!e.es_cliente,
        es_proveedor: !!e.es_proveedor,
        naturaleza_juridica: e.naturaleza_juridica || '',
        es_ute: !!e.es_ute,
        ambito_distribuidor: e.ambito_distribuidor || '',
        materiales_fabricados: e.materiales_fabricados || [],
        tipos_adicionales: e.tipos_adicionales || [],
        oficina_tecnica_notas: e.oficina_tecnica_notas || '',
        provincia: (e.provincia || '').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '),
        pais: e.pais || 'ES',
        localidad: e.localidad || '',
        direccion: e.direccion || '',
        codigo_postal: e.codigo_postal || '',
        telefono: e.telefono || '',
        email: e.email || '',
        web: e.web || '',
        division: e.division || '',
        comercial_asignado_id: e.comercial_asignado_id || '',
        notas: e.notas || '',
        es_delegacion: !!e.es_delegacion,
        parent_entidad_id: e.parent_entidad_id || '',
        tiene_deposito_nuestro: !!e.tiene_deposito_nuestro,
        es_almacen_logistico: !!e.es_almacen_logistico,
        // Enriquecimiento
        linkedin_url: e.linkedin_url || '',
        cnae: e.cnae || '',
        sector: e.sector || '',
        año_fundacion: e.año_fundacion || '',
        empleados_aprox: e.empleados_aprox || '',
        facturacion: e.facturacion || '',
        grupo_empresarial: e.grupo_empresarial || '',
        // Clasificación
        zona_comercial: e.zona_comercial || '',
        es_competencia: !!e.es_competencia,
        // UTE
        ute_socios_cif: e.ute_socios_cif || [],
        ute_socios_ids: e.ute_socios_ids || [],
        // Otros
        campos_personalizados: e.campos_personalizados || {},
        direcciones_entrega: e.direcciones_entrega || [],
      });
      const esDelegacion = !!e.es_delegacion;
      const [cs, acts, cts, vs, ts, deleg] = await Promise.all([
        contactosApi.list({ entidad_id: id, ...(esDelegacion ? {} : { incluir_delegaciones: true }) }).catch(() => []),
        entidadesApi.actividad(id, 3).catch(() => []),
        e.tipo === 'gestora'
          ? contratosApi.list({ gestora_id: id }).catch(() => [])
          : Promise.resolve([]),
        visitasApi.list({ entidad_id: id }).catch(() => []),
        tareasApi.list({ entidad_id: id }).catch(() => []),
        esDelegacion ? Promise.resolve([]) : entidadesApi.delegaciones(id).catch(() => []),
      ]);
      setContactos(cs);
      setDelegaciones(deleg);
      setActividad(acts);
      setContratos(cts);
      setVisitas(vs);
      setTareasCliente(ts);
    } catch (e) {
      alert(e.message);
      navigate('/clientes');
    }
  };

  const guardar = async (ev) => {
    ev?.preventDefault?.();
    setSaving(true); setErr('');
    setFormErrors({});
    try {
      const fe = {};
      if (!required(form.razon_social)) fe.razon_social = 'La razón social es obligatoria';
      if (!required(form.tipo)) fe.tipo = 'Selecciona un tipo';
      if (!required(form.pais)) fe.pais = 'Selecciona un país';
      if (!required(form.provincia)) fe.provincia = 'La provincia/distrito es obligatoria';
      const emailCambiado = isNuevo || form.email !== (entidad?.email || '');
      if (emailCambiado && !isValidEmail(form.email)) fe.email = 'Email no válido';
      if (!form.es_cliente && !form.es_proveedor) {
        fe.rol_comercial = 'Marca al menos "Cliente" o "Proveedor"';
      }
      if (Object.keys(fe).length) {
        setFormErrors(fe);
        const primero = fe.razon_social ? 'f-razon-social'
          : fe.tipo ? 'f-tipo'
          : fe.provincia ? 'f-provincia'
          : fe.email ? 'f-email-empresa'
          : fe.rol_comercial ? 'f-es-cliente' : null;
        const msg = Object.values(fe)[0];
        setErr(msg);
        toast.error(msg);
        if (primero) {
          const el = document.querySelector(`[data-testid="${primero}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setSaving(false);
        return;
      }
      const payload = { ...form };
      // Si no es fabricante, no enviamos materiales
      if (payload.tipo !== 'fabricante') payload.materiales_fabricados = [];
      ['cif','codigo_cliente_erp','grupo_id','localidad','direccion',
       'codigo_postal','telefono','email','web','comercial_asignado_id','notas',
       'naturaleza_juridica','ambito_distribuidor','oficina_tecnica_notas','division',
       'linkedin_url','cnae','sector','año_fundacion','empleados_aprox',
       'facturacion','grupo_empresarial','zona_comercial']
        .forEach((k) => {
          if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
        });

      if (isNuevo) {
        const e = await entidadesApi.create(payload);
        setEdit(false);
        toast.success(`Empresa "${e.razon_social || e.nombre_comercial || 'sin nombre'}" creada correctamente`);
        navigate(`/clientes/${e.id}`, { replace: true });
      } else {
        await entidadesApi.update(id, payload);
        setEdit(false);
        cargarEntidad();
      }
    } catch (e) {
      setErr(e.message);
      toast.error(`No se pudo guardar: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const desactivar = async () => {
    if (!window.confirm('¿Desactivar este cliente? Se marcará como inactivo.')) return;
    try { await entidadesApi.deactivate(id); navigate('/clientes'); }
    catch (e) { alert(e.message); }
  };

  // Contacto CRUD
  const abrirNuevoContacto = () => {
    setContactoEditing(null);
    setContactoForm(emptyContacto());
    setContactoDelegacionId('');
    setContactoErr(''); setContactoTabInner('principal');
    setContactoDialog(true);
  };
  const abrirEditarContacto = (c) => {
    setContactoEditing(c);
    setContactoForm({
      nombre: c.nombre || '',
      apellidos: c.apellidos || '',
      cargo: c.cargo || '',
      departamento: c.departamento || '',
      email_trabajo: c.email_trabajo || '',
      telefono_trabajo: c.telefono_trabajo || '',
      movil_trabajo: c.movil_trabajo || '',
      email_personal: c.email_personal || '',
      telefono_personal: c.telefono_personal || '',
      movil_personal: c.movil_personal || '',
      es_decisor: !!c.es_decisor,
      fecha_inicio_empleo: c.fecha_inicio_empleo || '',
      jefe_directo_id: c.jefe_directo_id || '',
      nivel_relacion: c.nivel_relacion || 'sin_definir',
      division: c.division || '',
      notas: c.notas || '',
    });
    setContactoErr(''); setContactoTabInner('principal');
    setContactoDialog(true);
  };
  const guardarContacto = async () => {
    setContactoErr('');
    if (!contactoForm.nombre?.trim()) { setContactoErr('El nombre es obligatorio'); return; }
    if (!isValidEmail(contactoForm.email_trabajo)) { setContactoErr('Email de trabajo no válido'); return; }
    if (!isValidEmail(contactoForm.email_personal)) { setContactoErr('Email personal no válido'); return; }
    try {
      const payload = {
        nombre: contactoForm.nombre,
        apellidos: contactoForm.apellidos || null,
        cargo: contactoForm.cargo || null,
        departamento: contactoForm.departamento || null,
        email_trabajo: contactoForm.email_trabajo || null,
        telefono_trabajo: contactoForm.telefono_trabajo || null,
        movil_trabajo: contactoForm.movil_trabajo || null,
        email_personal: contactoForm.email_personal || null,
        telefono_personal: contactoForm.telefono_personal || null,
        movil_personal: contactoForm.movil_personal || null,
        es_decisor: !!contactoForm.es_decisor,
        jefe_directo_id: contactoForm.jefe_directo_id || null,
        nivel_relacion: contactoForm.nivel_relacion || 'sin_definir',
        notas: contactoForm.notas || null,
        division: contactoForm.division || null,
      };
      if (contactoForm.fecha_inicio_empleo) payload.fecha_inicio_empleo = contactoForm.fecha_inicio_empleo;

      if (contactoEditing) {
        await contactosApi.update(contactoEditing.id, payload);
      } else {
        await contactosApi.create({ ...payload, entidad_id: contactoDelegacionId || id });
      }
      setContactoDialog(false);
      cargarEntidad();
    } catch (e) { setContactoErr(e.message); }
  };
  const borrarContacto = (c) => setConfirmDel({ open: true, item: c, tipo: 'contacto', nombre: c.nombre });
  // Sprint 3 — abre el dialog del contacto directamente en el subtab Correos
  const abrirCorreosContacto = (c) => { abrirEditarContacto(c); setContactoTabInner('correos'); };

  // Cambiar empresa
  const abrirCambioEmpresa = () => {
    if (!contactoEditing) return;
    setCambioErr('');
    setCambioEmpresaSearch('');
    setCambioEmpresaForm({ nueva_entidad_id: '', nuevo_cargo: contactoEditing.cargo || '', nuevo_departamento: contactoEditing.departamento || '', fecha_cambio: new Date().toISOString().slice(0,10) });
    setCambioEmpresaDialog(true);
  };
  const confirmarCambioEmpresa = async (ev) => {
    ev?.preventDefault?.();
    setCambioErr('');
    if (!cambioEmpresaForm.nueva_entidad_id) { setCambioErr('Selecciona la nueva empresa'); return; }
    if (!cambioEmpresaForm.fecha_cambio) { setCambioErr('Indica la fecha del cambio'); return; }
    try {
      await contactosApi.cambiarEmpresa(contactoEditing.id, {
        nueva_entidad_id: cambioEmpresaForm.nueva_entidad_id,
        nuevo_cargo: cambioEmpresaForm.nuevo_cargo || null,
        nuevo_departamento: cambioEmpresaForm.nuevo_departamento || null,
        fecha_cambio: cambioEmpresaForm.fecha_cambio,
      });
      setCambioEmpresaDialog(false);
      setContactoDialog(false);
      cargarEntidad();
    } catch (e) { setCambioErr(e.message); }
  };

  // Contratos (solo gestoras)
  const abrirNuevoContrato = () => {
    setContratoEditing(null);
    setContratoForm({
      nombre: '', codigo_interno: '', fecha_inicio: '', fecha_fin: '', estado: 'activo',
      proxima_licitacion_fecha: '', licitacion_gestion_notas: '', notas: '',
    });
    setContratoMunis([]); setMuniBuscarTexto(''); setMuniBuscarResult([]); setContratoErr('');
    setContratoDialog(true);
  };
  const abrirEditarContrato = (c) => {
    setContratoEditing(c);
    setContratoForm({
      nombre: c.nombre || '',
      codigo_interno: c.codigo_interno || '',
      fecha_inicio: c.fecha_inicio || '',
      fecha_fin: c.fecha_fin || '',
      estado: c.estado || 'activo',
      proxima_licitacion_fecha: c.proxima_licitacion_fecha || '',
      licitacion_gestion_notas: c.licitacion_gestion_notas || '',
      notas: c.notas || '',
    });
    setContratoMunis(c.municipios_detalle || []);
    setMuniBuscarTexto(''); setMuniBuscarResult([]); setContratoErr('');
    setContratoDialog(true);
  };
  const buscarMuni = async (texto) => {
    setMuniBuscarTexto(texto);
    if (texto.length < 2) { setMuniBuscarResult([]); return; }
    try {
      const res = await municipiosApi.list({ q: texto, limit: 15 });
      const actuales = new Set(contratoMunis.map((m) => m.codigo_ine));
      setMuniBuscarResult(res.filter((m) => !actuales.has(m.codigo_ine)));
    } catch { setMuniBuscarResult([]); }
  };
  const guardarContrato = async (ev) => {
    ev?.preventDefault?.();
    setContratoErr('');
    if (!contratoForm.nombre.trim()) { setContratoErr('El nombre es obligatorio'); return; }
    try {
      const payload = {
        nombre: contratoForm.nombre,
        codigo_interno: contratoForm.codigo_interno || null,
        fecha_inicio: contratoForm.fecha_inicio || null,
        fecha_fin: contratoForm.fecha_fin || null,
        estado: contratoForm.estado,
        proxima_licitacion_fecha: contratoForm.proxima_licitacion_fecha || null,
        licitacion_gestion_notas: contratoForm.licitacion_gestion_notas || null,
        notas: contratoForm.notas || null,
        municipios_ine: contratoMunis.map((m) => m.codigo_ine),
      };
      if (contratoEditing) {
        await contratosApi.update(contratoEditing.id, payload);
      } else {
        await contratosApi.create({ ...payload, gestora_id: id });
      }
      setContratoDialog(false);
      cargarEntidad();
    } catch (e) { setContratoErr(e.message); }
  };
  const borrarContrato = (c) => setConfirmDel({ open: true, item: c, tipo: 'contrato', nombre: c.nombre });

  // Quick-create grupo
  const crearGrupoRapido = async (ev) => {
    ev.preventDefault();
    if (!grupoForm.nombre.trim()) { setGrupoErr('El nombre es obligatorio'); return; }
    setSavingGrupo(true); setGrupoErr('');
    try {
      const nuevo = await gruposApi.create({
        nombre: grupoForm.nombre.trim(),
        descripcion: grupoForm.descripcion || null,
      });
      const lista = await gruposApi.list();
      setGrupos(lista);
      setForm((f) => ({ ...f, grupo_id: nuevo.id }));
      setGrupoDialog(false);
    } catch (e) { setGrupoErr(e.message); }
    finally { setSavingGrupo(false); }
  };

  // Visitas CRUD
  const abrirNuevaVisita = () => { setVisitaEditing(null); setVisitaDialog(true); };
  const abrirEditarVisita = (v) => { setVisitaEditing(v); setVisitaDialog(true); };
  const guardarVisita = async (payload) => {
    if (visitaEditing) await visitasApi.update(visitaEditing.id, payload);
    else await visitasApi.create(id, payload);
    setVisitaEditing(null);
    cargarEntidad();
  };
  const borrarVisita = (v) => setConfirmDel({ open: true, item: v, tipo: 'visita', nombre: `visita del ${v.fecha}` });

  const abrirNuevaTarea = (visitaSrc = null) => {
    setTareaSourceVisita(visitaSrc);
    setTareaEditing(null);
    const defaultTitulo = visitaSrc
      ? (visitaSrc.siguientes_pasos
          ? visitaSrc.siguientes_pasos.split('\n')[0].slice(0, 100)
          : `Seguimiento visita ${visitaSrc.fecha}`)
      : '';
    setTareaForm({
      titulo: defaultTitulo,
      descripcion: visitaSrc?.siguientes_pasos || '',
      vencimiento: visitaSrc?.proxima_accion_fecha || '',
      asignado_user_id: user?.id || '',
      prioridad: 'media',
    });
    setTareaErr(''); setTareaDialog(true);
  };
  const abrirEditarTarea = (t) => {
    setTareaSourceVisita(null);
    setTareaEditing(t);
    setTareaForm({
      titulo: t.titulo || '',
      descripcion: t.descripcion || '',
      vencimiento: t.vencimiento || '',
      asignado_user_id: t.asignado_user_id || '',
      prioridad: t.prioridad || 'media',
    });
    setTareaErr(''); setTareaDialog(true);
  };
  const borrarTareaCliente = (t) => setConfirmDel({ open: true, item: t, tipo: 'tarea', nombre: t.titulo });
  const guardarTarea = async (ev) => {
    ev?.preventDefault?.();
    setTareaErr('');
    if (!tareaForm.titulo?.trim()) { setTareaErr('El título es obligatorio'); return; }
    if (!tareaForm.asignado_user_id) { setTareaErr('Selecciona responsable'); return; }
    try {
      if (tareaEditing) {
        await tareasApi.update(tareaEditing.id, {
          titulo: tareaForm.titulo,
          descripcion: tareaForm.descripcion || null,
          vencimiento: tareaForm.vencimiento || null,
          asignado_user_id: tareaForm.asignado_user_id,
          prioridad: tareaForm.prioridad,
        });
      } else {
        await tareasApi.create({
          titulo: tareaForm.titulo,
          descripcion: tareaForm.descripcion || null,
          vencimiento: tareaForm.vencimiento || null,
          asignado_user_id: tareaForm.asignado_user_id,
          prioridad: tareaForm.prioridad,
          entidad_id: id,
          visita_id: tareaSourceVisita?.id || null,
        });
      }
      setTareaDialog(false);
      cargarEntidad();
    } catch (e) { setTareaErr(e.message); }
  };

  // Organigrama: construir árbol por jefe_directo_id
  const organigrama = useMemo(() => buildTree(contactos), [contactos]);

  const tabs = [
    { k: 'resumen',   l: 'Resumen 360°',       icon: LayoutDashboard },
    { k: 'datos',     l: 'Datos',             icon: FileText },
    { k: 'contactos', l: `Contactos (${contactos.length})`, icon: UsersIcon },
    ...(esGestora ? [{ k: 'contratos', l: `Contratos (${contratos.length})`, icon: FileStack }] : []),
    { k: 'ventas',    l: 'Ventas 360°',       icon: BarChart3 },
    ...(!entidad?.es_delegacion ? [{ k: 'delegaciones', l: `Delegaciones${entidad?.num_delegaciones > 0 ? ` (${entidad.num_delegaciones})` : ''}`, icon: GitBranch }] : []),
    { k: 'visitas',   l: `Visitas (${visitas.length})`, icon: CalendarDays },
    { k: 'tareas',    l: `Tareas (${tareasCliente.filter((t) => t.estado !== 'hecha' && t.estado !== 'cancelada').length})`, icon: ListChecks },
    { k: 'historial', l: 'Historial', icon: History },
    ...(hasPermission('correos', 'leer') ? [{ k: 'correos', l: 'Correos', icon: Mail }] : []),
    { k: 'actividad', l: 'Actividad reciente', icon: Activity },
  ];

  return (
    <div data-testid="cliente-detalle">
      <Link to="/clientes" className="inline-flex items-center text-sm text-ink-500 hover:text-ink-800 mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Volver a empresas
      </Link>

      {/* Header */}
      <Card className="p-6 mb-6 border-ink-200 bg-white">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0 overflow-hidden relative group">
              {entidad?.logo_url ? (
                <img src={resolveAssetUrl(entidad.logo_url)} alt="" className="h-full w-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <Building2 className="w-7 h-7" />
              )}
              {edit && !isNuevo && (
                <label className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 text-white text-[10px] cursor-pointer transition" data-testid="btn-logo-upload-label">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    data-testid="input-logo-upload"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        const fd = new FormData();
                        fd.append('fichero', f);
                        await entidadesApi.subirLogo(id, fd);
                        toast.success('Logo subido');
                        await cargarEntidad();
                      } catch (err) { toast.error(err.message); }
                      finally { e.target.value = ''; }
                    }}
                  />
                  Cambiar
                </label>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-ink-900 leading-tight">
                {isNuevo ? 'Nueva empresa' : (entidad?.nombre_comercial || entidad?.razon_social || '—')}
              </h1>
              <p className="text-sm text-ink-500 mt-1">
                {entidad?.cif && <>CIF <span className="font-mono">{entidad.cif}</span></>}
                {entidad?.codigo_cliente_erp && <> · ERP <span className="font-mono">{entidad.codigo_cliente_erp}</span></>}
              </p>
              {entidad && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Badge className={`${ESTADO_STYLE[entidad.estado]} border`}>{entidad.estado}</Badge>
                  <Badge variant="outline" className="bg-white border-ink-200 text-ink-700">
                    <Briefcase className="w-3 h-3 mr-1" /> {TIPO_LABEL[entidad.tipo] || entidad.tipo}
                  </Badge>
                  {entidad.naturaleza_juridica && (
                    <Badge variant="outline" className="bg-white border-ink-200 text-ink-700">
                      {entidad.naturaleza_juridica}
                    </Badge>
                  )}
                  {entidad.es_ute && <Badge className="bg-violet-50 text-violet-700 border border-violet-200">UTE</Badge>}
                  <span className="inline-flex items-center gap-1 text-sm text-ink-600">
                    <MapPin className="w-3.5 h-3.5 text-ink-400" />
                    {entidad.provincia}{entidad.localidad && ` · ${entidad.localidad}`}
                  </span>
                  {entidad.grupo_nombre && (
                    <span className="inline-flex items-center gap-1 text-sm text-ink-500">
                      · Grupo: <span className="text-ink-700 font-medium">{entidad.grupo_nombre}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          {!isNuevo && (
            <div className="flex gap-2">
              {!edit ? (
                <>
                  <Button variant="outline" onClick={() => setEdit(true)} data-testid="btn-editar">
                    <Pencil className="w-4 h-4 mr-1.5" /> Editar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPplxOpen(true)}
                    className="border-violet-200 text-violet-700 hover:bg-violet-50"
                    data-testid="btn-pplx-empresa"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" /> Buscar con Perplexity
                  </Button>
                  {isAdmin && entidad?.estado !== 'inactivo' && (
                    <Button variant="outline" onClick={desactivar} data-testid="btn-desactivar"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200">
                      Desactivar
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { setEdit(false); cargarEntidad(); }}>
                    <X className="w-4 h-4 mr-1" /> Cancelar
                  </Button>
                  <Button className="bg-brand-500 hover:bg-brand-600 text-white" onClick={guardar} disabled={saving} data-testid="btn-guardar">
                    <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando…' : 'Guardar'}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Tabs */}
      {!isNuevo && (
        <div className="flex gap-1 mb-6 border-b border-ink-200 overflow-x-auto" data-testid="cliente-tabs">
          {tabs.map(({ k, l, icon: I }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === k
                  ? 'border-brand-500 text-brand-700'
                  : 'border-transparent text-ink-500 hover:text-ink-800'
              }`}
              data-testid={`tab-${k}`}
            >
              <I className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
      )}

      {/* UTE Trazabilidad — siempre visible si aplica */}
      {!isNuevo && entidad && (
        <UteTrazabilidad entidadId={id} esUte={!!entidad.es_ute} />
      )}

      {/* Breadcrumb empresa matriz cuando es delegación */}
      {!isNuevo && entidad?.es_delegacion && entidad?.parent_entidad_id && (
        <div className="mb-4 flex items-center gap-2 text-sm text-ink-500">
          <GitBranch className="w-4 h-4 text-brand-500" />
          <span>Delegación de</span>
          <button
            onClick={() => navigate(`/clientes/${entidad.parent_entidad_id}`)}
            className="text-brand-600 hover:underline font-medium"
          >
            {entidad.parent_entidad_nombre || 'empresa matriz'}
          </button>
        </div>
      )}

      {/* Delegaciones */}
      {!isNuevo && tab === 'delegaciones' && entidad && !entidad.es_delegacion && (
        <DelegacionesTab entidadId={id} parentNombre={entidad.nombre_comercial || entidad.razon_social || ''} />
      )}

      {/* Resumen 360° */}
      {!isNuevo && tab === 'resumen' && entidad && (
        <Vista360
          entidadId={id}
          entidad={entidad}
          contactos={contactos}
          contratos={contratos}
          visitas={visitas}
          tareas={tareasCliente}
          delegaciones={delegaciones}
          onChangeTab={setTab}
        />
      )}

      {/* Datos */}
      {(isNuevo || tab === 'datos') && (
        <DatosTab
          form={form}
          setForm={setForm}
          edit={edit}
          formErrors={formErrors}
          err={err}
          saving={saving}
          isNuevo={isNuevo}
          isAdmin={isAdmin}
          entidad={entidad}
          materiales={materiales}
          grupos={grupos}
          comerciales={comerciales}
          paisesExtra={paisesExtra}
          provinciasPorPais={provinciasPorPais}
          onGuardar={guardar}
          onAbrirGrupo={() => { setGrupoForm({ nombre: '', descripcion: '' }); setGrupoErr(''); setGrupoDialog(true); }}
          onAbrirPplx={() => setPplxOpen(true)}
          onCancelarNuevo={() => navigate('/clientes')}
        />
      )}

      {/* Contactos */}
      {!isNuevo && tab === 'contactos' && (
        <ContactosTab
          contactos={contactos}
          organigrama={organigrama}
          onNuevo={abrirNuevoContacto}
          onEditar={abrirEditarContacto}
          onBorrar={borrarContacto}
          onVerCorreos={abrirCorreosContacto}
          entidadId={id}
        />
      )}

      {/* Contratos (solo gestoras) */}
      {!isNuevo && tab === 'contratos' && esGestora && (
        <ContratosTab
          contratos={contratos}
          entidad={entidad}
          onNuevo={abrirNuevoContrato}
          onEditar={abrirEditarContrato}
          onBorrar={borrarContrato}
        />
      )}

      {/* Ventas 360° (Sprint S5) */}
      {!isNuevo && tab === 'ventas' && (
        <div data-testid="tab-ventas-content">
          <VentasFicha360 entidadId={id} />
        </div>
      )}

      {/* Visitas */}
      {!isNuevo && tab === 'visitas' && (
        <VisitasTab
          visitas={visitas}
          onNueva={abrirNuevaVisita}
          onEditar={abrirEditarVisita}
          onBorrar={borrarVisita}
          onDerivarTarea={(v) => abrirNuevaTarea(v)}
        />
      )}

      {/* Tareas del cliente */}
      {!isNuevo && tab === 'tareas' && (
        <TareasTab
          tareasCliente={tareasCliente}
          onNueva={() => abrirNuevaTarea(null)}
          onEditar={abrirEditarTarea}
          onBorrar={borrarTareaCliente}
          onToggleEstado={toggleTareaEstado}
        />
      )}

      {/* Historial */}
      {!isNuevo && tab === 'historial' && (
        <HistorialTab entidadId={id} />
      )}

      {/* Correos */}
      {!isNuevo && tab === 'correos' && hasPermission('correos', 'leer') && (
        <Card className="p-6 border-ink-200 bg-white" data-testid="tab-correos-content">
          <div className="mb-4">
            <h3 className="font-heading font-semibold text-ink-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-brand-600" /> Correos intercambiados
            </h3>
            <p className="text-xs text-ink-500 mt-1">
              Mensajes vinculados automáticamente a esta empresa por dominio o email de contactos.
            </p>
          </div>
          <CorreosTab entidadId={id} contextLabel={entidad?.razon_social || entidad?.nombre_comercial} />
        </Card>
      )}

      {/* Actividad */}
      {!isNuevo && tab === 'actividad' && (
        <ActividadTab actividad={actividad} ActividadItem={ActividadItem} />
      )}

      {/* Dialog Contacto */}
      <Dialog open={contactoDialog} onOpenChange={(o) => { if (!o) setContactoDialog(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-contacto">
          <DialogHeader>
            <DialogTitle>{contactoEditing ? 'Editar contacto' : 'Añadir contacto'}</DialogTitle>
            <DialogDescription>
              {contactoEditing
                ? <>Empleado actual de <span className="font-semibold">{entidad?.nombre_comercial || entidad?.razon_social}</span>.</>
                : <>Nuevo empleado de <span className="font-semibold">{entidad?.nombre_comercial || entidad?.razon_social}</span>.</>
              }
            </DialogDescription>
          </DialogHeader>

          {/* Sub-tabs contacto */}
          <div className="flex gap-1 border-b border-ink-200 mt-2">
            <SubTab active={contactoTabInner === 'principal'} onClick={() => setContactoTabInner('principal')} icon={UserCog} label="Principal" />
            {isPrivileged && (
              <SubTab active={contactoTabInner === 'personal'} onClick={() => setContactoTabInner('personal')} icon={ShieldAlert} label="Personal" />
            )}
            {contactoEditing && contactoEditing.historico_empleos?.length > 0 && (
              <SubTab active={contactoTabInner === 'trayectoria'} onClick={() => setContactoTabInner('trayectoria')} icon={History} label={`Trayectoria (${contactoEditing.historico_empleos.length})`} />
            )}
            {contactoEditing && hasPermission('correos', 'leer') && (
              <SubTab active={contactoTabInner === 'correos'} onClick={() => setContactoTabInner('correos')} icon={Mail} label="Correos" />
            )}
          </div>

          {contactoTabInner === 'correos' && contactoEditing && hasPermission('correos', 'leer') && (
            <div className="pt-3 space-y-3" data-testid="contacto-correos-tab">
              <div className="flex flex-wrap items-center gap-2">
                <ResumenContactoIA contactoId={contactoEditing.id} />
                <PreguntarBuzon
                  contactoEmail={contactoEditing.email_trabajo || contactoEditing.email_personal}
                  contextLabel={`${contactoEditing.nombre || ''} ${contactoEditing.apellidos || ''}`.trim()}
                />
              </div>
              <CorreosTab
                contactoEmail={contactoEditing.email_trabajo || contactoEditing.email_personal}
                contextLabel={`${contactoEditing.nombre || ''} ${contactoEditing.apellidos || ''}`.trim()}
              />
            </div>
          )}

          {contactoTabInner === 'principal' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
              {/* Selector de delegación — solo al crear nuevo contacto desde la empresa madre */}
              {!contactoEditing && delegaciones.length > 0 && (
                <Campo label="Delegación" wide>
                  <select
                    className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3"
                    value={contactoDelegacionId}
                    onChange={(e) => setContactoDelegacionId(e.target.value)}
                    data-testid="cf-delegacion"
                  >
                    <option value="">— Empresa principal —</option>
                    {delegaciones.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.razon_social}{d.localidad ? ` · ${d.localidad}` : ''}
                      </option>
                    ))}
                  </select>
                </Campo>
              )}
              <Campo label="Nombre *">
                <Input value={contactoForm.nombre} onChange={(e) => setContactoForm({ ...contactoForm, nombre: e.target.value })} data-testid="cf-nombre" />
              </Campo>
              <Campo label="Apellidos">
                <Input value={contactoForm.apellidos} onChange={(e) => setContactoForm({ ...contactoForm, apellidos: e.target.value })} />
              </Campo>
              <Campo label="Cargo">
                <Input value={contactoForm.cargo} onChange={(e) => setContactoForm({ ...contactoForm, cargo: e.target.value })} />
              </Campo>
              <Campo label="Departamento">
                <Select
                  value={contactoForm.departamento}
                  onChange={(v) => setContactoForm({ ...contactoForm, departamento: v })}
                  options={[{ v: '', l: '— Sin departamento —' }, ...departamentos.map((d) => ({ v: d.valor, l: d.label }))]}
                />
              </Campo>
              <Campo label="División interna">
                <Input
                  value={contactoForm.division || ''}
                  onChange={(e) => setContactoForm({ ...contactoForm, division: e.target.value })}
                  placeholder="Ej: Levante, Centro, Norte…"
                  data-testid="input-contacto-division"
                />
              </Campo>
              <Campo label="Email de trabajo">
                <Input type="email" value={contactoForm.email_trabajo} onChange={(e) => setContactoForm({ ...contactoForm, email_trabajo: e.target.value })} />
              </Campo>
              <Campo label="Teléfono de trabajo">
                <Input value={contactoForm.telefono_trabajo} onChange={(e) => setContactoForm({ ...contactoForm, telefono_trabajo: e.target.value })} />
              </Campo>
              <Campo label="Móvil de trabajo">
                <Input value={contactoForm.movil_trabajo} onChange={(e) => setContactoForm({ ...contactoForm, movil_trabajo: e.target.value })} />
              </Campo>
              <Campo label="Fecha inicio empleo">
                <Input type="date" value={contactoForm.fecha_inicio_empleo || ''} onChange={(e) => setContactoForm({ ...contactoForm, fecha_inicio_empleo: e.target.value })} />
              </Campo>
              <Campo label="Jefe directo" wide>
                <Select
                  value={contactoForm.jefe_directo_id}
                  onChange={(v) => setContactoForm({ ...contactoForm, jefe_directo_id: v })}
                  options={[{ v: '', l: '— Sin jefe directo —' }, ...contactos
                    .filter((c) => !contactoEditing || c.id !== contactoEditing.id)
                    .map((c) => ({ v: c.id, l: `${c.nombre} ${c.apellidos || ''}${c.cargo ? ' · ' + c.cargo : ''}` }))]}
                />
              </Campo>
              <div className="sm:col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  id="cf-decisor"
                  className="accent-brand-500"
                  checked={!!contactoForm.es_decisor}
                  onChange={(e) => setContactoForm({ ...contactoForm, es_decisor: e.target.checked })}
                />
                <label htmlFor="cf-decisor" className="text-ink-700">Es persona decisora / clave en la compra</label>
              </div>
              <Campo label="Nivel de relación" wide>
                <div className="flex flex-wrap gap-2" data-testid="cf-nivel-relacion">
                  {NIVELES_RELACION.map((n) => {
                    const sel = contactoForm.nivel_relacion === n.v;
                    return (
                      <button
                        key={n.v}
                        type="button"
                        onClick={() => setContactoForm({ ...contactoForm, nivel_relacion: n.v })}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all ${
                          sel
                            ? 'bg-ink-100 border-ink-400 text-ink-900 font-semibold'
                            : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'
                        }`}
                        data-testid={`cf-nivel-${n.v}`}
                      >
                        <span className={`w-2.5 h-2.5 rounded-full ${n.dot}`} />
                        {n.l}
                      </button>
                    );
                  })}
                </div>
              </Campo>
              <Campo label="Notas" wide>
                <Textarea rows={2} value={contactoForm.notas} onChange={(e) => setContactoForm({ ...contactoForm, notas: e.target.value })} />
              </Campo>
            </div>
          )}

          {contactoTabInner === 'personal' && isPrivileged && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
              <div className="sm:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Datos personales — visibles solo para Admin / Dir. Comercial. No se mostrarán a los agentes comerciales.</span>
              </div>
              <Campo label="Email personal">
                <Input type="email" value={contactoForm.email_personal} onChange={(e) => setContactoForm({ ...contactoForm, email_personal: e.target.value })} />
              </Campo>
              <Campo label="Móvil personal">
                <Input value={contactoForm.movil_personal} onChange={(e) => setContactoForm({ ...contactoForm, movil_personal: e.target.value })} />
              </Campo>
              <Campo label="Teléfono personal" wide>
                <Input value={contactoForm.telefono_personal} onChange={(e) => setContactoForm({ ...contactoForm, telefono_personal: e.target.value })} />
              </Campo>
            </div>
          )}

          {contactoTabInner === 'trayectoria' && contactoEditing?.historico_empleos?.length > 0 && (
            <div className="pt-3">
              <ul className="space-y-3">
                {[...contactoEditing.historico_empleos].reverse().map((h, i) => (
                  <li key={i} className="border border-ink-200 rounded-md p-3 bg-ink-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-ink-900 text-sm">{h.entidad_nombre_snapshot || '—'}</p>
                        <p className="text-xs text-ink-500">{h.cargo || '—'}{h.departamento && ` · ${h.departamento}`}</p>
                      </div>
                      <div className="text-[11px] text-ink-500 text-right font-mono">
                        {formatDate(h.desde)} → {formatDate(h.hasta) || 'hoy'}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {contactoErr && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm mt-2">{contactoErr}</div>}

          <DialogFooter className="mt-2 flex-wrap">
            {contactoEditing && (
              <Button type="button" variant="outline" onClick={abrirCambioEmpresa} className="mr-auto text-brand-700 border-brand-200 hover:bg-brand-50" data-testid="btn-cambiar-empresa">
                <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Cambiar de empresa
              </Button>
            )}
            <Button variant="outline" onClick={() => setContactoDialog(false)}>Cancelar</Button>
            <Button className="bg-brand-500 hover:bg-brand-600 text-white" onClick={guardarContacto} data-testid="btn-guardar-contacto">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Cambiar Empresa */}
      <Dialog open={cambioEmpresaDialog} onOpenChange={(o) => { if (!o) { setCambioEmpresaDialog(false); setCambioEmpresaSearch(''); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-cambiar-empresa">
          <form onSubmit={confirmarCambioEmpresa}>
            <DialogHeader>
              <DialogTitle>Cambiar de empresa</DialogTitle>
              <DialogDescription>
                Se archivará el empleo actual en <span className="font-semibold">{entidad?.nombre_comercial || entidad?.razon_social}</span> y se asignará el contacto a la nueva empresa.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Campo label="Nueva empresa *" required>
                <EmpresaAutocomplete
                  value={cambioEmpresaSearch}
                  onChange={(v) => {
                    setCambioEmpresaSearch(v);
                    if (!v.trim()) setCambioEmpresaForm((f) => ({ ...f, nueva_entidad_id: '' }));
                  }}
                  placeholder="Escribe nombre, CIF o localidad…"
                  onPick={(it) => {
                    setCambioEmpresaSearch(it.razon_social);
                    setCambioEmpresaForm((f) => ({ ...f, nueva_entidad_id: it.id }));
                  }}
                  testid="cambio-empresa-select"
                />
              </Campo>
              <Campo label="Nuevo cargo">
                <Input value={cambioEmpresaForm.nuevo_cargo} onChange={(e) => setCambioEmpresaForm({ ...cambioEmpresaForm, nuevo_cargo: e.target.value })} />
              </Campo>
              <Campo label="Nuevo departamento">
                <Select
                  value={cambioEmpresaForm.nuevo_departamento}
                  onChange={(v) => setCambioEmpresaForm({ ...cambioEmpresaForm, nuevo_departamento: v })}
                  options={[{ v: '', l: '— Sin departamento —' }, ...departamentos.map((d) => ({ v: d.valor, l: d.label }))]}
                />
              </Campo>
              <Campo label="Fecha del cambio *" required>
                <Input type="date" value={cambioEmpresaForm.fecha_cambio} onChange={(e) => setCambioEmpresaForm({ ...cambioEmpresaForm, fecha_cambio: e.target.value })} data-testid="cambio-empresa-fecha" />
              </Campo>
              {cambioErr && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{cambioErr}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCambioEmpresaDialog(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-confirmar-cambio-empresa">Confirmar cambio</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Contrato */}
      <Dialog open={contratoDialog} onOpenChange={(o) => { if (!o) setContratoDialog(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-contrato">
          <form onSubmit={guardarContrato}>
            <DialogHeader>
              <DialogTitle>{contratoEditing ? 'Editar contrato' : 'Nuevo contrato'}</DialogTitle>
              <DialogDescription>
                Contrato de gestión operado por <span className="font-semibold">{entidad?.nombre_comercial || entidad?.razon_social}</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
              <Campo label="Nombre *" required wide>
                <Input value={contratoForm.nombre} onChange={(e) => setContratoForm({ ...contratoForm, nombre: e.target.value })} data-testid="ct-nombre" />
              </Campo>
              <Campo label="Código interno">
                <Input value={contratoForm.codigo_interno} onChange={(e) => setContratoForm({ ...contratoForm, codigo_interno: e.target.value })} />
              </Campo>
              <Campo label="Estado">
                <Select value={contratoForm.estado} onChange={(v) => setContratoForm({ ...contratoForm, estado: v })}
                  options={[
                    { v: 'prospecto', l: 'Prospecto' },
                    { v: 'en_licitacion', l: 'En licitación' },
                    { v: 'activo', l: 'Activo' },
                    { v: 'prorrogado', l: 'Prorrogado' },
                    { v: 'finalizado', l: 'Finalizado' },
                  ]}
                />
              </Campo>
              <Campo label="Fecha inicio">
                <Input type="date" value={contratoForm.fecha_inicio || ''} onChange={(e) => setContratoForm({ ...contratoForm, fecha_inicio: e.target.value })} />
              </Campo>
              <Campo label="Fecha fin">
                <Input type="date" value={contratoForm.fecha_fin || ''} onChange={(e) => setContratoForm({ ...contratoForm, fecha_fin: e.target.value })} data-testid="ct-fecha-fin" />
              </Campo>
              <Campo label="Próxima licitación" wide>
                <Input type="date" value={contratoForm.proxima_licitacion_fecha || ''} onChange={(e) => setContratoForm({ ...contratoForm, proxima_licitacion_fecha: e.target.value })} />
              </Campo>
              <Campo label="Notas licitación" wide>
                <Textarea rows={2} value={contratoForm.licitacion_gestion_notas} onChange={(e) => setContratoForm({ ...contratoForm, licitacion_gestion_notas: e.target.value })} />
              </Campo>

              {/* Municipios */}
              <div className="sm:col-span-2 border-t border-ink-100 pt-3">
                <label className="text-xs font-medium text-ink-600 mb-1 block">Municipios incluidos</label>
                {contratoMunis.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2" data-testid="ct-munis">
                    {contratoMunis.map((m) => (
                      <span key={m.codigo_ine} className="inline-flex items-center gap-1 text-xs bg-brand-50 border border-brand-200 text-brand-700 rounded px-2 py-1">
                        <MapPinned className="w-3 h-3" /> {m.nombre} ({m.provincia})
                        <button type="button" onClick={() => setContratoMunis(contratoMunis.filter((x) => x.codigo_ine !== m.codigo_ine))} className="ml-1 text-brand-700 hover:text-red-600" title="Quitar">×</button>
                      </span>
                    ))}
                  </div>
                )}
                <Input placeholder="Buscar municipio (min 2 letras)…" value={muniBuscarTexto} onChange={(e) => buscarMuni(e.target.value)} data-testid="ct-muni-buscar" />
                {muniBuscarResult.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-y-auto border border-ink-200 rounded-md bg-white" data-testid="ct-muni-result">
                    {muniBuscarResult.map((m) => (
                      <button
                        type="button"
                        key={m.codigo_ine}
                        onClick={() => { setContratoMunis([...contratoMunis, m]); setMuniBuscarTexto(''); setMuniBuscarResult([]); }}
                        className="w-full text-left px-3 py-1.5 hover:bg-ink-50 text-sm border-b border-ink-100 last:border-0"
                      >
                        <span className="font-medium">{m.nombre}</span> <span className="text-ink-500 text-xs">· {m.provincia} · {m.codigo_ine}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Campo label="Notas internas" wide>
                <Textarea rows={2} value={contratoForm.notas} onChange={(e) => setContratoForm({ ...contratoForm, notas: e.target.value })} />
              </Campo>
              {contratoErr && <div className="sm:col-span-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{contratoErr}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContratoDialog(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-guardar-contrato">
                Guardar contrato
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Visita */}
      <VisitaFormDialog
        open={visitaDialog}
        onOpenChange={(o) => { if (!o) { setVisitaDialog(false); setVisitaEditing(null); } }}
        entidad_id={id}
        entidad_nombre={entidad?.nombre_comercial || entidad?.razon_social}
        onSave={guardarVisita}
        initialData={visitaEditing}
        contactos_empresa={contactos}
        onTasksCreated={recargarTareas}
      />


      {/* Dialog Tarea (desde visita o libre) */}
      <Dialog open={tareaDialog} onOpenChange={(o) => { if (!o) setTareaDialog(false); }}>
        <DialogContent className="max-w-md" data-testid="dialog-tarea-cliente">
          <form onSubmit={guardarTarea}>
            <DialogHeader>
              <DialogTitle>{tareaEditing ? 'Editar tarea' : (tareaSourceVisita ? 'Tarea derivada de visita' : 'Nueva tarea')}</DialogTitle>
              <DialogDescription>
                {tareaEditing
                  ? <>Modifica los campos y guarda los cambios.</>
                  : tareaSourceVisita
                    ? <>Se generará vinculada a la visita del <span className="font-semibold">{tareaSourceVisita.fecha}</span> y al cliente.</>
                    : <>Tarea asociada a este cliente.</>}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Campo label="Título *" required>
                <Input value={tareaForm.titulo} onChange={(e) => setTareaForm({ ...tareaForm, titulo: e.target.value })} autoFocus data-testid="tcf-titulo" />
              </Campo>
              <Campo label="Descripción">
                <Textarea rows={3} value={tareaForm.descripcion} onChange={(e) => setTareaForm({ ...tareaForm, descripcion: e.target.value })} />
              </Campo>
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Vencimiento">
                  <Input type="date" value={tareaForm.vencimiento} onChange={(e) => setTareaForm({ ...tareaForm, vencimiento: e.target.value })} />
                </Campo>
                <Campo label="Prioridad">
                  <Select value={tareaForm.prioridad} onChange={(v) => setTareaForm({ ...tareaForm, prioridad: v })}
                    options={[{ v: 'baja', l: 'Baja' }, { v: 'media', l: 'Media' }, { v: 'alta', l: 'Alta' }]}
                  />
                </Campo>
              </div>
              <Campo label="Responsable *" required>
                <Select
                  value={tareaForm.asignado_user_id}
                  onChange={(v) => setTareaForm({ ...tareaForm, asignado_user_id: v })}
                  options={[{ v: '', l: '— Elegir —' }, ...usuariosAll.map((u) => ({ v: u.id, l: `${u.nombre} ${u.apellidos || ''}${u.rol ? ' · ' + u.rol : ''}` }))]}
                  testid="tcf-asignado"
                />
              </Campo>
              {tareaErr && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{tareaErr}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTareaDialog(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-guardar-tarea-cliente">{tareaEditing ? 'Guardar' : 'Crear tarea'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      <PerplexityBuscarModal
        open={pplxOpen}
        onOpenChange={setPplxOpen}
        entidad={isNuevo ? form : entidad}
        onApply={async (patch) => {
          if (isNuevo) {
            setForm((f) => ({ ...f, ...patch }));
            toast.success(`${Object.keys(patch).length} campos cargados desde Perplexity — revisa y pulsa "Crear empresa"`);
          } else {
            try {
              await entidadesApi.update(id, patch);
              toast.success(`Aplicados ${Object.keys(patch).length} campos desde Perplexity`);
              await cargarEntidad();
            } catch (e) { toast.error(e.message); }
          }
        }}
      />


      {/* Dialog contacto rápido (desde visita — añadir asistente que no existe) */}
      <Dialog open={grupoDialog} onOpenChange={(o) => { if (!o) setGrupoDialog(false); }}>
        <DialogContent className="max-w-md" data-testid="dialog-grupo-quick">
          <form onSubmit={crearGrupoRapido}>
            <DialogHeader>
              <DialogTitle>Nuevo grupo empresarial</DialogTitle>
              <DialogDescription>
                Se creará y quedará seleccionado en este cliente.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Campo label="Nombre *">
                <Input value={grupoForm.nombre} onChange={(e) => setGrupoForm({ ...grupoForm, nombre: e.target.value })} placeholder="Ej: Grupo Agbar" autoFocus data-testid="grupo-quick-nombre" />
              </Campo>
              <Campo label="Descripción">
                <Textarea rows={2} value={grupoForm.descripcion} onChange={(e) => setGrupoForm({ ...grupoForm, descripcion: e.target.value })} />
              </Campo>
              {grupoErr && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{grupoErr}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGrupoDialog(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" disabled={savingGrupo} data-testid="btn-crear-grupo-submit">
                {savingGrupo ? 'Creando…' : 'Crear grupo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel((s) => ({ ...s, open: o }))}
        resource={confirmDel.tipo || 'elemento'}
        itemName={confirmDel.nombre}
        onConfirm={confirmarBorrarUnificado}
        testidPrefix="confirm-del-cliente"
      />
    </div>
  );
};

export default ClienteDetalle;
